<script lang="ts">
  /**
   * What needs doing.
   *
   * Jobs arrive from three places and this screen does not care which: a schedule
   * coming due, Vault filing a warranty that is about to run out, or Hindsight
   * noticing the borehole ran twice as long as usual. Hindsight already POSTs here
   * — `HOMESTEAD_API_URL` was unset, so its findings were recorded and went
   * nowhere. This is where they land.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";

  type Job = {
    id: string;
    assetId: string | null;
    assetName: string | null;
    title: string;
    detail: string;
    raisedBy: string;
    raisedAt: string | null;
    dueAt: string | null;
    severity: "info" | "warn";
    status: string;
    evidence: { source: string; url: string | null; summary: string } | null;
  };

  let jobs = $state<Job[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let busy = $state<string | null>(null);
  let adding = $state(false);
  let title = $state("");
  let detail = $state("");
  let dueAt = $state("");

  const open = $derived(jobs.filter((j) => j.status === "open" || j.status === "scheduled"));

  async function load() {
    loading = true;
    error = null;
    try {
      jobs = (await api.get<{ jobs: Job[] }>("/jobs")).jobs;
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not load the job list.";
    } finally {
      loading = false;
    }
  }
  void load();

  async function close(j: Job, status: "done" | "dismissed") {
    busy = j.id;
    try {
      await api.patch(`/jobs/${j.id}`, { status });
      await load();
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not update that.";
    } finally {
      busy = null;
    }
  }

  async function add() {
    if (!title.trim()) {
      error = "A title is required.";
      return;
    }
    busy = "new";
    try {
      await api.post("/jobs", { title: title.trim(), detail: detail.trim(), dueAt: dueAt || null });
      title = "";
      detail = "";
      dueAt = "";
      adding = false;
      await load();
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not add that.";
    } finally {
      busy = null;
    }
  }

  const SOURCE: Record<string, string> = {
    schedule: "a service schedule",
    vault: "a document filed in Vault",
    hindsight: "something Hindsight noticed",
    // F18. Named separately from `schedule` on purpose: a season is not an
    // interval from the last time it was done, and "raised by the time of year"
    // is the sentence that stops someone rescheduling the firebreaks for
    // twelve months' time.
    season: "the time of year",
    manual: "you",
  };

  function days(d: string): number {
    return Math.round((Date.parse(d) - Date.now()) / 86_400_000);
  }
</script>

<div class="wrap">
  <section class="card">
    <div class="head">
      <span class="kicker">What needs doing</span>
      <span class="sub">{open.length} open</span>
    </div>

    {#if loading}
      <p class="sub pad">Loading…</p>
    {:else if error}
      <p class="err pad"><Icon name="alert" size={12} /> {error}</p>
    {:else if open.length === 0}
      <p class="sub pad">
        <Icon name="check" size={12} /> Nothing outstanding. Jobs appear here when a service
        falls due, when Vault files a warranty that is running out, or when Hindsight sees
        something behaving oddly.
      </p>
    {:else}
      {#each open as j (j.id)}
        {@const overdue = j.dueAt ? days(j.dueAt) < 0 : false}
        <div class="row" class:row--warn={j.severity === "warn" || overdue}>
          <Icon name={j.severity === "warn" || overdue ? "alert" : "wrench"} size={14} />
          <div class="grow">
            <div class="row__t">{j.title}</div>
            {#if j.detail}<div class="sub">{j.detail}</div>{/if}
            <div class="chips">
              {#if j.assetName}<span class="pill pill--quiet">{j.assetName}</span>{/if}
              <span class="pill pill--quiet">raised by {SOURCE[j.raisedBy] ?? j.raisedBy}</span>
              {#if j.dueAt}
                <span class="pill" class:pill--warn={overdue}>
                  <span class="num">{dateZA(j.dueAt)}</span>
                  {overdue ? `· ${Math.abs(days(j.dueAt))} days overdue` : `· in ${days(j.dueAt)} days`}
                </span>
              {/if}
            </div>
            {#if j.evidence}
              <!-- Evidence is shown rather than summarised away: a job you cannot
                   check is a job you will not action. -->
              <div class="sub ev">
                <!-- `dot`, not `info`: there is no `info` in Icon.svelte's map and
                     an unknown name renders an empty <svg>, so this line has been
                     shipping with an invisible glyph beside it. Adding a glyph to
                     the map is the other legitimate fix; a bullet is what this
                     line actually wants. -->
                <Icon name="dot" size={11} />
                {j.evidence.summary}
                {#if j.evidence.url}<a href={j.evidence.url} target="_blank" rel="noreferrer">look</a>{/if}
              </div>
            {/if}
          </div>
          <div class="rowacts">
            <button class="lnk" disabled={busy === j.id} onclick={() => close(j, "dismissed")}>Not needed</button>
            <button class="btn-primary sm" disabled={busy === j.id} onclick={() => close(j, "done")}>Done</button>
          </div>
        </div>
      {/each}
    {/if}

    {#if adding}
      <div class="edit">
        <div class="grid">
          <label class="f f--wide">
            <span>What needs doing</span>
            <input bind:value={title} placeholder="Service the borehole pump" />
          </label>
          <label class="f f--wide">
            <span>Detail</span>
            <input bind:value={detail} placeholder="Anything the person doing it would want to know" />
          </label>
          <label class="f"><span>Due</span><input type="date" bind:value={dueAt} /></label>
        </div>
        <div class="acts">
          <button class="lnk" onclick={() => (adding = false)} disabled={busy === "new"}>Cancel</button>
          <button class="btn-primary" onclick={add} disabled={busy === "new"}>
            {busy === "new" ? "Adding…" : "Add it"}
          </button>
        </div>
      </div>
    {:else}
      <div class="acts">
        <button class="btn-primary" onclick={() => (adding = true)}>Add a job</button>
      </div>
    {/if}
  </section>
</div>

<style>
  .wrap { display: flex; flex-direction: column; gap: 14px; }
  .card {
    padding: 16px 18px 18px;
    border: 1px solid var(--ln);
    border-radius: var(--r-card, 12px);
    background: var(--surface-1, var(--bg));
  }
  .head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 12px; }
  .kicker { font-size: 11px; font-weight: 800; letter-spacing: 0.8px; text-transform: uppercase; color: var(--acc); }
  .pad { padding: 8px 0; }
  .row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 11px 12px;
    border: 1px solid var(--ln);
    border-radius: var(--r-control);
    margin-bottom: 6px;
  }
  /* A left edge rather than a fill: it survives being read by someone who cannot
     tell amber from grey, and the glyph says the same thing again. */
  .row--warn { border-left: 3px solid var(--warn); }
  .grow { flex: 1; min-width: 0; }
  .row__t { font-size: 13px; font-weight: 700; color: var(--tx); }
  .chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 6px; }
  .pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 7px;
    font-size: 10.5px;
    font-weight: 700;
    border-radius: 999px;
    background: var(--surface-2, var(--bg));
    border: 1px solid var(--ln);
    color: var(--tx);
  }
  .pill--quiet { color: var(--mut); }
  .pill--warn { color: var(--warn); }
  .num { font-variant-numeric: tabular-nums; }
  .ev { margin-top: 6px; display: flex; align-items: center; gap: 5px; }
  .ev a { color: var(--acc); font-weight: 700; }
  .rowacts { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
  .edit {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 10px;
    padding: 14px 16px 16px;
    border: 1px solid var(--ln);
    border-radius: var(--r-card, 12px);
    background: var(--surface-2, var(--bg));
  }
  .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; }
  .f { display: flex; flex-direction: column; gap: 4px; font-size: 11.5px; font-weight: 700; color: var(--mut); }
  .f--wide { grid-column: 1 / -1; }
  .f input {
    padding: 8px 10px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--tx);
    background: var(--bg);
    border: 1px solid var(--ln);
    border-radius: var(--r-control);
  }
  .acts { display: flex; justify-content: flex-end; align-items: center; gap: 12px; margin-top: 8px; }
  .lnk { font-size: 11.5px; font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }
  .sm { padding: 6px 12px; font-size: 11.5px; }
  .err { display: inline-flex; align-items: center; gap: 5px; margin: 0; font-size: 11.5px; font-weight: 700; color: var(--warn); }
  .sub { font-size: 11.5px; color: var(--mut); }
</style>
